import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/motion/Reveal";
import { Quote, Check } from "lucide-react";
import { Eyebrow, Button } from "@/components/kit";

const points = [
  "Gespecialiseerd in MKB & ZZP",
  "Direct contact — geen accountmanagers",
  "AI-versneld, menselijk gemaakt",
  "Transparant, elke maand opnieuw",
];

const FOUNDER_IMG = "/team/tim-brussel.webp";

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], ["-6%", "8%"]);

  return (
    <section
      id="over"
      ref={ref}
      data-testid="about-section"
      className="relative bg-ivory py-24 lg:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-8 lg:gap-16 items-center">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-5"
          >
            <div className="relative max-w-md mx-auto lg:mx-0">
              <div className="absolute -inset-3 rounded-[1.75rem] border border-electric/30 rotate-[1.5deg] pointer-events-none" />
              <div className="absolute -bottom-5 -left-5 w-28 h-28 rounded-2xl bg-electric/10 blur-2xl" />
              <div className="relative aspect-[4/5] rounded-[1.5rem] overflow-hidden bg-obsidian-800 shadow-card-lift">
                <motion.img
                  style={{ y: portraitY }}
                  src={FOUNDER_IMG}
                  alt="Tim Roosendaal — oprichter van UPPIT"
                  className="absolute inset-0 w-full h-[112%] object-cover object-[center_30%]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/85 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-electric-300 mb-1">
                    Oprichter &amp; strateeg
                  </div>
                  <div className="font-display font-bold text-3xl text-ivory leading-none">
                    Tim Roosendaal
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Text */}
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <Eyebrow>Over UPPIT · De mens achter het merk</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 flex items-start gap-4">
                <Quote className="w-8 h-8 text-electric shrink-0 mt-1.5 -scale-x-100" />
                <p className="font-display font-bold text-3xl lg:text-[2.6rem] text-coal leading-[1.08] tracking-tight text-balance">
                  Ik geloof niet in bureau-onzin. Ik geloof in{" "}
                  <span className="text-gradient-blue">werk dat klanten oplevert</span>{" "}
                  — en in gesprekken die dat waarmaken.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl mt-8 text-coal/65 leading-relaxed">
                <p>
                  UPPIT is een boutique-bureau voor MKB en ZZP. Klein team,
                  grote obsessie. AI is onze snelheid, mensen zijn onze richting.
                </p>
                <p>
                  Geen dashboards zonder duiding. Geen contracten zonder uitweg.
                  Je krijgt Tim aan tafel — de rest is uitvoering.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <ul className="grid sm:grid-cols-2 gap-3 max-w-xl border-t border-ivory-400 pt-8 mt-8">
                {points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-coal/80">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-electric/10 grid place-items-center shrink-0">
                      <Check className="w-3 h-3 text-electric" />
                    </span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.4} className="mt-9">
              <Button to="/contact" variant="light" withArrow testid="about-cta">
                Maak kennis met Tim
              </Button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
